import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { isSplitSize } from '../config/layout';

/**
 * Mesure la coquille d'une page et dit si elle a la place pour deux colonnes.
 *
 * On mesure la coquille elle-meme (hauteur 100%, c'est elle qui defile) : sa
 * largeur est deja plafonnee par PageShell, donc les deux pages decident sur
 * la meme valeur.
 *
 * `active` : une page cachee (display none) mesure 0, on garde alors la
 * derniere mesure connue au lieu de retomber en colonne unique.
 */
const useSplitLayout = (ref, active = true) => {
  const [size, setSize] = useState({ width: 0, height: 0 });
  const splitRef = useRef(false);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    // Ecrit ici, en phase de layout : un effet passif qui lit splitRef joue
    // avant le re-rendu declenche par setSize, donc avant que `split` soit a
    // jour.
    splitRef.current = isSplitSize(rect.width, rect.height);
    setSize((prev) => (
      prev.width === rect.width && prev.height === rect.height
        ? prev
        : { width: rect.width, height: rect.height }
    ));
  }, [ref]);

  useLayoutEffect(() => {
    measure();
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => measure());
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure, ref]);

  // La coquille redevient visible quand la page redevient active : on remesure.
  useLayoutEffect(() => {
    if (active) measure();
  }, [active, measure]);

  return {
    width: size.width,
    height: size.height,
    split: isSplitSize(size.width, size.height),
    splitRef,
    measure
  };
};

export default useSplitLayout;
